<!--
  AI:
  Status: 'in progress'
  Type: Component
  SubType: Presentation
  Reason: A-2 — the „Verlauf“ of one signing process as a p-timeline. Marker, icon and colour of a
    row come from the backend's own @UiEnumValueInfo metadata (SmTimelineEntryDto.valueListEntry);
    this template contains no colour, icon or label table of its own. Read-only.
-->
<div class="sm-timeline-container p-3">

    <!-- Heading. Its wording is the service's constant — the surface states what it holds and makes
         no completeness claim, which is a binding condition of the signed design. -->
    <div class="flex align-items-center justify-content-between gap-3 mb-3">
        <h5 class="m-0 font-semibold">
            <i class="fa-regular fa-clock-rotate-left mr-2 text-primary"></i>{{ heading }}
        </h5>
        @if (state === 'ready' || state === 'empty') {
            <p-button icon="fa-regular fa-arrows-rotate"
                      severity="secondary"
                      size="small"
                      [text]="true"
                      [rounded]="true"
                      pTooltip="Verlauf neu laden"
                      (onClick)="load()">
            </p-button>
        }
    </div>

    <!-- LOADING. Skeletons rather than a spinner: the shape of what is coming is already known, and
         a placeholder in that shape does not make the surface jump when the rows arrive. -->
    @if (state === 'loading') {
        <div class="flex flex-column gap-2" role="status" [attr.aria-label]="loadingText">
            <span class="text-sm text-color-secondary">{{ loadingText }}</span>
            <p-skeleton width="100%" height="3rem" borderRadius="0.5rem"></p-skeleton>
            <p-skeleton width="100%" height="3rem" borderRadius="0.5rem"></p-skeleton>
            <p-skeleton width="100%" height="3rem" borderRadius="0.5rem"></p-skeleton>
        </div>
    }

    <!-- ERROR. Separated from the empty state on purpose: „hier ist noch nichts geschehen“ and „die
         Abfrage ist fehlgeschlagen“ are opposite statements and must never share their wording. -->
    @if (state === 'error') {
        <div class="flex flex-column align-items-start gap-3">
            <p-message severity="error" [text]="errorText" styleClass="w-full"></p-message>
            <p-button label="Erneut laden"
                      icon="fa-regular fa-arrows-rotate"
                      severity="secondary"
                      size="small"
                      (onClick)="load()">
            </p-button>
        </div>
    }

    <!-- EMPTY -->
    @if (state === 'empty') {
        <div class="surface-card border-round p-4 text-center">
            <i class="fa-regular fa-clock-rotate-left text-4xl text-color-secondary mb-3"></i>
            <p class="text-color-secondary mt-2 mb-0">{{ emptyText }}</p>
        </div>
    }

    @if (state === 'ready') {

        <!-- The signer lanes, named and coloured once above the track, so a reader learns the
             vocabulary before meeting it in a row. The chip repeats on every row as well: a lane
             told apart by hue alone is unreadable for a colour blind reader and invisible in a
             greyscale printout, which is the form a history like this is most often read in. -->
        @if (lanes.length > 0) {
            <div class="flex flex-wrap align-items-center gap-2 mb-3">
                <span class="text-sm text-color-secondary">Spuren:</span>
                @for (lane of lanes; track lane.signerId) {
                    <span class="border-round px-2 py-1 text-sm font-medium"
                          [ngClass]="laneChipClassOf(lane.signerId)">
                        {{ laneCaption(lane) }}
                        @if (lane.signerStatusValueListEntry?.label) {
                            <span class="ml-1 text-xs">· {{ lane.signerStatusValueListEntry.label }}</span>
                        }
                    </span>
                }
            </div>
        }

        <p-timeline [value]="entries" layout="vertical" align="left" styleClass="sm-timeline">

            <!-- WHEN. dd.MM.yyyy HH:mm — the repo's date convention, the same one the details card
                 of this object uses. -->
            <ng-template #opposite let-entry>
                <small class="text-color-secondary white-space-nowrap">
                    {{ entry.timestamp | date:'dd.MM.yyyy HH:mm' }}
                </small>
            </ng-template>

            <!-- MARKER. Icon and colours are the BACKEND's, served through the QL pipe's form as
                 MvsFormValueListEntryDto. A row whose source column carries no value list gets a
                 neutral bubble with no icon — the gap is never filled from a table in the frontend. -->
            <ng-template #marker let-entry>
                <span class="flex align-items-center justify-content-center border-circle shadow-1 w-2rem h-2rem"
                      [ngClass]="markerClass(entry)"
                      [attr.title]="entry.valueListEntry?.description || entry.title">
                    @if (markerIcon(entry)) {
                        <i [class]="markerIcon(entry)" class="text-sm"></i>
                    }
                </span>
            </ng-template>

            <!-- THE CARD.
                 A row the backend declared as needing attention gets its OWN outline around the card
                 plus a warning sign — a shape and a symbol, not merely another hue, so the marking
                 survives a greyscale printout and a colour blind reader. The lane keeps its coloured
                 left edge inside that outline, so the two statements do not overwrite each other. -->
            <ng-template #content let-entry>
                <div class="border-round mb-2"
                     [ngClass]="isException(entry) ? 'border-1 border-red-300 bg-red-50 p-1' : ''">

                    <div class="surface-card border-round p-3 border-left-3"
                         [ngClass]="laneBorderClass(entry)">

                        <div class="flex flex-wrap align-items-center gap-2 mb-1">

                            <!--
                              fa-SOLID, and the only solid icon in this file. UX7 defaults to
                              fa-regular and admits fa-solid for state and importance, which is
                              exactly what this glyph is: it is not decoration on a row, it is the
                              mark that separates a row needing attention from its neighbours. A
                              hairline outline of the same shape reads as one more row at the
                              glance this surface is scanned with — and on a greyscale printout,
                              where the red is gone, the filled shape is the only thing left of the
                              distinction. Do not "correct" it to fa-regular.
                            -->
                            @if (isException(entry)) {
                                <i class="fa-solid fa-triangle-exclamation text-red-600"
                                   title="Dieser Eintrag benötigt Aufmerksamkeit"></i>
                            }

                            <span class="font-medium">{{ entry.title }}</span>

                            <!-- The lane, as a word next to its colour. -->
                            <span class="border-round px-2 py-1 text-xs font-medium"
                                  [ngClass]="laneChipClass(entry)">
                                {{ laneLabel(entry) }}
                            </span>

                            <!-- The row's own enum label, exactly as the backend spells it. -->
                            @if (entry.valueListEntry?.label && entry.valueListEntry.label !== entry.title) {
                                <span class="border-round px-2 py-1 text-xs"
                                      [ngClass]="markerClass(entry)">
                                    {{ entry.valueListEntry.label }}
                                </span>
                            }
                        </div>

                        @if (entry.detail) {
                            <div class="text-sm text-color-secondary">{{ entry.detail }}</div>
                        }

                        <!--
                          The single affordance of a row, and it is a read. Versand, „Link neu
                          ausstellen“ und Abbruch stay in „Unterzeichner“: this surface is what a
                          caseworker reads to reconstruct what happened, and a screen that both
                          explains the past and changes it invites a change made while reading.
                        -->
                        <div class="flex justify-content-end mt-2">
                            <p-button label="Öffnen"
                                      icon="fa-regular fa-arrow-up-right-from-square"
                                      iconPos="right"
                                      severity="secondary"
                                      size="small"
                                      [text]="true"
                                      [attr.aria-label]="'Datensatz zu „' + entry.title + '“ öffnen'"
                                      (onClick)="openSource(entry)">
                            </p-button>
                        </div>
                    </div>
                </div>
            </ng-template>
        </p-timeline>
    }
</div>
